/**
 * TY SHOP - Core JavaScript
 * Global functionality for the entire theme.
 * Handles: Cart (Shopify AJAX API), Wishlist (localStorage),
 *          Toast notifications, Quantity selectors, Utility functions.
 *
 * Organized on the window.TYShop namespace initialized in theme.liquid.
 * Vanilla JS only — no external dependencies.
 */

(function () {
  'use strict';

  /* Merge into the namespace already seeded by theme.liquid */
  var TYShop = window.TYShop || {};

  /* ============================================================
     TYShop.Cart — Shopify AJAX API
     Endpoints: /cart/add.js  /cart/change.js  /cart.js
     ============================================================ */

  TYShop.Cart = {

    /**
     * Add a variant to the cart.
     * @param {string|number} variantId  Shopify variant ID
     * @param {number}        quantity   Default 1
     * @param {Object}        properties Optional line-item properties
     * @returns {Promise<Object|null>} Line-item data or null on error
     */
    async add(variantId, quantity, properties) {
      quantity = quantity || 1;
      properties = properties || {};

      try {
        var response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: variantId,
            quantity: quantity,
            properties: properties
          })
        });

        if (!response.ok) {
          var errData = await response.json().catch(function () { return {}; });
          throw new Error(errData.description || 'Errore nell\'aggiunta al carrello');
        }

        var item = await response.json();
        TYShop.Toast.success('\u201c' + item.title + '\u201d aggiunto al carrello');
        var cart = await TYShop.Cart.get();
        if (cart) TYShop.Cart.updateBadge(cart.item_count);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        return item;

      } catch (error) {
        TYShop.Toast.error(error.message || 'Errore nell\'aggiunta al carrello');
        /* error already shown via toast */
        return null;
      }
    },

    /**
     * Update an existing line item's quantity.
     * Setting quantity to 0 removes the item.
     * @param {string} key      Shopify cart line-item key
     * @param {number} quantity New quantity (0 = remove)
     * @returns {Promise<Object|null>} Full cart object or null on error
     */
    async update(key, quantity) {
      try {
        var response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: quantity })
        });

        if (!response.ok) {
          var errData = await response.json().catch(function () { return {}; });
          throw new Error(errData.description || 'Errore nell\'aggiornamento del carrello');
        }

        var cart = await response.json();
        TYShop.Cart.updateBadge(cart.item_count);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        return cart;

      } catch (error) {
        TYShop.Toast.error(error.message || 'Errore nell\'aggiornamento del carrello');
        /* error already shown via toast */
        return null;
      }
    },

    /**
     * Remove a line item from the cart.
     * @param {string} key Shopify cart line-item key
     * @returns {Promise<Object|null>} Full cart object or null on error
     */
    async remove(key) {
      var cart = await TYShop.Cart.update(key, 0);
      if (cart) TYShop.Toast.show('Prodotto rimosso dal carrello', 'info');
      return cart;
    },

    /**
     * Fetch the current cart from Shopify.
     * @returns {Promise<Object|null>} Cart object or null on error
     */
    async get() {
      try {
        var response = await fetch('/cart.js', {
          headers: { 'Content-Type': 'application/json' }
        });
        if (!response.ok) throw new Error('Impossibile recuperare il carrello');
        return await response.json();
      } catch (error) {
        /* silent fail — cart badge won't update */
        return null;
      }
    },

    /**
     * Update all cart-count badges in the header / anywhere in the DOM.
     * @param {number} count Total item count
     */
    updateBadge(count) {
      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.textContent = count;
        el.style.display = count > 0 ? '' : 'none';
      });
    }
  };

  /* ============================================================
     TYShop.Wishlist — localStorage persistence
     ============================================================ */

  TYShop.Wishlist = {

    /** localStorage key */
    KEY: 'tyshop_wishlist',

    /**
     * Return the full array of saved product handles.
     * @returns {string[]}
     */
    getAll() {
      try {
        return JSON.parse(localStorage.getItem(this.KEY)) || [];
      } catch (e) {
        return [];
      }
    },

    /** Persist handles array and refresh UI. */
    _save(handles) {
      localStorage.setItem(this.KEY, JSON.stringify(handles));
      this.updateBadge();
      document.dispatchEvent(new CustomEvent('wishlist:changed', {
        detail: { handles: handles }
      }));
    },

    /**
     * Add a product handle to the wishlist (no duplicates).
     * @param {string} handle Shopify product handle
     */
    add(handle) {
      var items = this.getAll();
      if (!items.includes(handle)) {
        items.push(handle);
        this._save(items);
        TYShop.Toast.success('Aggiunto alla wishlist');
      }
      this.updateIcons();
    },

    /**
     * Remove a product handle from the wishlist.
     * @param {string} handle Shopify product handle
     */
    remove(handle) {
      var items = this.getAll().filter(function (h) { return h !== handle; });
      this._save(items);
      TYShop.Toast.show('Rimosso dalla wishlist', 'info');
      this.updateIcons();
    },

    /**
     * Toggle the wishlist state for a handle.
     * @param {string} handle
     */
    toggle(handle) {
      if (this.has(handle)) {
        this.remove(handle);
      } else {
        this.add(handle);
      }
    },

    /**
     * Check whether a product handle is in the wishlist.
     * @param {string} handle
     * @returns {boolean}
     */
    has(handle) {
      return this.getAll().includes(handle);
    },

    /**
     * Update all [data-wishlist-count] badge elements.
     */
    updateBadge() {
      var count = this.getAll().length;
      document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
        el.textContent = count;
        el.style.display = count > 0 ? '' : 'none';
      });
    },

    /**
     * Reflect wishlist state on all [data-wishlist-toggle] icon buttons.
     * Adds/removes the `is-active` class based on localStorage state.
     */
    updateIcons() {
      var self = this;
      document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
        var handle = btn.dataset.productHandle;
        if (!handle) return;
        if (self.has(handle)) {
          btn.classList.add('is-active');
          btn.setAttribute('aria-pressed', 'true');
        } else {
          btn.classList.remove('is-active');
          btn.setAttribute('aria-pressed', 'false');
        }
      });
    }
  };

  /* ============================================================
     TYShop.Toast — Notification system
     Requires #toast-container in the DOM (added by theme.liquid).
     ============================================================ */

  TYShop.Toast = {

    /**
     * Display a toast notification.
     * @param {string} message  Text to display
     * @param {string} type     'success' | 'error' | 'info'  (default: 'success')
     * @param {number} duration Auto-dismiss delay in ms        (default: 3000)
     */
    show(message, type, duration) {
      type = type || 'success';
      duration = duration !== undefined ? duration : 3000;

      var container = document.getElementById('toast-container');
      if (!container) return;

      /* Icon map */
      var icons = { success: '✓', error: '✕', info: 'ℹ' };
      var icon = icons[type] || icons.success;

      var toast = document.createElement('div');
      toast.className = 'toast toast--' + type;
      toast.setAttribute('role', 'alert');
      toast.setAttribute('aria-live', 'polite');

      toast.innerHTML =
        '<span class="toast__icon" aria-hidden="true">' + icon + '</span>' +
        '<span class="toast__message">' + TYShop.Toast._escape(message) + '</span>' +
        '<button class="toast__close" aria-label="Chiudi notifica">&times;</button>';

      /* Close button — click and keyboard */
      var closeBtn = toast.querySelector('.toast__close');
      function dismiss() { TYShop.Toast._dismiss(toast); }
      closeBtn.addEventListener('click', dismiss);
      closeBtn.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); dismiss(); }
      });

      container.appendChild(toast);

      /* Trigger entrance animation on next frame */
      requestAnimationFrame(function () {
        toast.classList.add('toast--visible');
      });

      /* Auto-dismiss */
      if (duration > 0) {
        setTimeout(dismiss, duration);
      }
    },

    /**
     * Show a success toast.
     * @param {string} message
     */
    success(message) {
      this.show(message, 'success');
    },

    /**
     * Show an error toast.
     * @param {string} message
     */
    error(message) {
      this.show(message, 'error');
    },

    /** @private Animate out and remove a toast element. */
    _dismiss(toast) {
      if (!toast || !toast.parentNode) return;
      toast.classList.remove('toast--visible');
      toast.classList.add('toast--exit');
      setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 300);
    },

    /** @private Escape HTML in toast messages to prevent XSS. */
    _escape(str) {
      var div = document.createElement('div');
      div.appendChild(document.createTextNode(str));
      return div.innerHTML;
    }
  };

  /* ============================================================
     TYShop.QuantitySelector — +/- controls for quantity inputs
     Uses event delegation — works for dynamically added elements.
     Expected markup:
       <div data-quantity-selector>
         <button data-qty-minus>-</button>
         <input  data-qty-value type="number" value="1" min="1" max="99">
         <button data-qty-plus>+</button>
       </div>
     ============================================================ */

  TYShop.QuantitySelector = {

    MIN: 1,
    MAX: 99,

    /**
     * Bind delegated click + keyboard listeners on the document.
     * Call once on DOMContentLoaded.
     */
    init() {
      document.addEventListener('click', function (e) {
        TYShop.QuantitySelector._handleButton(e);
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          TYShop.QuantitySelector._handleButton(e);
        }
      });

      /* Clamp typed quantities to the input's max (giacenza) */
      document.addEventListener('change', function (e) {
        TYShop.QuantitySelector._handleTyped(e);
      });
    },

    /** @private Shared handler for click and keyboard events on qty buttons. */
    _handleButton(e) {
      var minusBtn = e.target.closest('[data-qty-minus]');
      var plusBtn  = e.target.closest('[data-qty-plus]');
      if (!minusBtn && !plusBtn) return;

      e.preventDefault();

      var selector = (minusBtn || plusBtn).closest('[data-quantity-selector]');
      if (!selector) return;

      var input = selector.querySelector('[data-qty-value]');
      if (!input) return;

      var maxAttr = parseInt(input.getAttribute('max'), 10);
      var max = (maxAttr && maxAttr > 0) ? maxAttr : TYShop.QuantitySelector.MAX;
      var current = parseInt(input.value, 10) || TYShop.QuantitySelector.MIN;
      var next;

      if (minusBtn) {
        next = Math.max(TYShop.QuantitySelector.MIN, current - 1);
      } else {
        next = Math.min(max, current + 1);
        if (next === current && max < TYShop.QuantitySelector.MAX && TYShop.Toast) {
          TYShop.Toast.show('Solo ' + max + ' disponibili', 'info');
        }
      }

      if (next !== current) {
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    },

    /** @private Clamp a manually typed quantity to [MIN, input max]. */
    _handleTyped(e) {
      var input = e.target.closest('[data-qty-value]');
      if (!input) return;
      var maxAttr = parseInt(input.getAttribute('max'), 10);
      var max = (maxAttr && maxAttr > 0) ? maxAttr : TYShop.QuantitySelector.MAX;
      var val = parseInt(input.value, 10);
      if (isNaN(val) || val < TYShop.QuantitySelector.MIN) val = TYShop.QuantitySelector.MIN;
      if (val > max) {
        val = max;
        if (max < TYShop.QuantitySelector.MAX && TYShop.Toast) {
          TYShop.Toast.show('Solo ' + max + ' disponibili', 'info');
        }
      }
      if (String(val) !== input.value) input.value = val;
    }
  };

  /* ============================================================
     TYShop.formatMoney — Money formatting helper
     Uses window.TYShop.settings.moneyFormat when available,
     falling back to a simple EUR format.
     ============================================================ */

  TYShop.formatMoney = function (cents) {
    var format = (TYShop.settings && TYShop.settings.moneyFormat) || '\u20ac{{amount}}';
    var amount  = (cents / 100).toFixed(2).replace('.', ',');
    return format
      .replace('{{amount}}', amount)
      .replace('{{amount_with_comma_separator}}', amount);
  };

  /* ============================================================
     TYShop.init — Bootstrap all modules
     ============================================================ */

  TYShop.init = function () {

    /* 1. Quantity selectors */
    TYShop.QuantitySelector.init();

    /* 2. Wishlist badge + icon states */
    TYShop.Wishlist.updateBadge();
    TYShop.Wishlist.updateIcons();

    /* 3. Cart badge — hydrate from live cart */
    TYShop.Cart.get().then(function (cart) {
      if (cart) TYShop.Cart.updateBadge(cart.item_count);
    });

    /* --------------------------------------------------------
       Add-to-cart — event delegation for [data-add-to-cart]
       Looks for a sibling [data-qty-value] input inside the
       nearest .product-card or .product-form ancestor.
       -------------------------------------------------------- */
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-add-to-cart]');
      if (!btn) return;
      e.preventDefault();

      var variantId = btn.dataset.variantId;
      if (!variantId) return;

      var container  = btn.closest('.product-card, .product-form');
      var qtyInput   = container ? container.querySelector('[data-qty-value]') : null;
      var qty        = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
      if (qtyInput) {
        var _maxA = parseInt(qtyInput.getAttribute('max'), 10);
        if (_maxA && _maxA > 0 && qty > _maxA) { qty = _maxA; qtyInput.value = _maxA; }
      }
      var origHTML = btn.innerHTML;

      btn.disabled = true;
      btn.classList.add('product-card__cart-btn--loading');

      TYShop.Cart.add(variantId, qty).then(function () {
        btn.disabled = false;
        btn.classList.remove('product-card__cart-btn--loading');
        // Brief success feedback
        btn.classList.add('product-card__cart-btn--success');
        setTimeout(function () {
          btn.classList.remove('product-card__cart-btn--success');
        }, 1200);
      });
    });

    /* Keyboard support for add-to-cart */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var btn = e.target.closest('[data-add-to-cart]');
      if (btn) { e.preventDefault(); btn.click(); }
    });

    /* --------------------------------------------------------
       Wishlist toggle — event delegation for [data-wishlist-toggle]
       Expects data-product-handle on the button element.
       -------------------------------------------------------- */
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();

      var handle = btn.dataset.productHandle;
      if (!handle) return;

      if (TYShop.Wishlist.has(handle)) {
        TYShop.Wishlist.remove(handle);
      } else {
        TYShop.Wishlist.add(handle);
      }
      TYShop.Wishlist.updateIcons();
    });

    /* Keyboard support for wishlist toggle */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var btn = e.target.closest('[data-wishlist-toggle]');
      if (btn) { e.preventDefault(); btn.click(); }
    });
  };

  /* Expose the fully built namespace */
  window.TYShop = TYShop;

  /* Run init after the DOM is ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', TYShop.init);
  } else {
    TYShop.init();
  }

})();
